/**
 * QR Code Scanner Utility
 * 
 * Centralized QR code scanning using html5-qrcode library.
 * Provides a reliable, cross-browser camera-based QR code reader.
 * 
 * Usage:
 * ```jsx
 * import { QRScanner } from 'utils/qrScanner';
 * 
 * // In a React component:
 * const scanner = useRef(null);
 * 
 * useEffect(() => {
 *   scanner.current = new QRScanner('qr-reader-element-id');
 *   scanner.current.start({
 *     onSuccess: (decodedText) => console.log('Scanned:', decodedText),
 *     onError: (error) => console.error('Scan error:', error),
 *   });
 *   
 *   return () => scanner.current?.stop();
 * }, []);
 * ```
 */

import { Html5Qrcode, Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Default configuration for QR scanning
const DEFAULT_CONFIG = {
  fps: 30, // Optimal FPS for good detection speed and device compatibility
  qrbox: function(viewfinderWidth, viewfinderHeight) {
    // Dynamic qrbox sizing - 70% of the smaller dimension
    let minEdgePercentage = 0.7;
    let minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
    let qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
    return {
      width: qrboxSize,
      height: qrboxSize
    };
  },
  aspectRatio: 1.777778, // 16:9 aspect ratio for better camera compatibility
  disableFlip: false, // Enable both orientations
  formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
  // Additional settings for better detection
  experimentalFeatures: {
    useBarCodeDetectorIfSupported: true // Use native barcode detection if available (much faster!)
  },
  rememberLastUsedCamera: true,
  showTorchButtonIfSupported: true, // Show flashlight button if available
};

// Scanner states
export const SCANNER_STATE = {
  NOT_STARTED: 'NOT_STARTED',
  SCANNING: 'SCANNING',
  PAUSED: 'PAUSED',
  STOPPED: 'STOPPED',
};

/**
 * QRScanner - Main QR code scanner class
 * 
 * Wraps html5-qrcode for easier use in React components.
 */
export class QRScanner {
  constructor(elementId, config = {}) {
    this.elementId = elementId;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.html5Qrcode = null;
    this.state = SCANNER_STATE.NOT_STARTED;
    this.onSuccessCallback = null;
    this.onErrorCallback = null;
  }

  /**
   * Start the QR code scanner
   * 
   * @param {Object} options
   * @param {Function} options.onSuccess - Callback when QR code is successfully scanned
   * @param {Function} options.onError - Callback when scan fails or camera error occurs
   * @param {string} options.cameraId - Specific camera ID to use (optional)
   * @param {boolean} options.preferBackCamera - Whether to prefer back camera (default: true)
   * @returns {Promise<void>}
   */
  async start(options) {
    const {
      onSuccess,
      onError,
      cameraId = null,
      preferBackCamera = true,
    } = options || {};
    this.onSuccessCallback = onSuccess;
    this.onErrorCallback = onError;

    try {
      // Create scanner instance
      this.html5Qrcode = new Html5Qrcode(this.elementId);
      
      // Determine camera to use with flexible constraints
      let cameraConfig;
      
      if (cameraId) {
        // Use specific camera
        cameraConfig = cameraId;
      } else if (preferBackCamera) {
        // Use back camera (environment facing) - 'ideal' not 'exact' for better compatibility
        cameraConfig = { 
          facingMode: 'environment' // Flexible: will use back camera if available, won't fail if not
        };
      } else {
        // Use front camera (user facing)
        cameraConfig = { 
          facingMode: 'user'
        };
      }

      // Start scanning
      await this.html5Qrcode.start(
        cameraConfig,
        this.config,
        (decodedText, decodedResult) => {
          
          this.onSuccessCallback?.(decodedText, decodedResult);
        },
        (errorMessage) => {
          // This fires continuously while scanning - only log actual errors
          if (!errorMessage.includes('No QR code found') && 
              !errorMessage.includes('NotFoundException')) {
            console.warn('[QRScanner] Scan error:', errorMessage);
          }
        }
      );

      this.state = SCANNER_STATE.SCANNING;

      this.state = SCANNER_STATE.SCANNING;

    } catch (err) {
      console.error('[QRScanner] Failed to start scanner:', err);
      this.state = SCANNER_STATE.STOPPED;
      
      // Translate error messages
      let userMessage = 'Failed to access camera';
      
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        userMessage = 'Camera access denied. Please allow camera access in your browser settings.';
      } else if (err.name === 'NotFoundError' || err.message?.includes('Requested device not found')) {
        userMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.message?.includes('Could not start video source')) {
        userMessage = 'Camera is in use by another application. Please close other apps using the camera.';
      } else if (err.message?.includes('HTTPS')) {
        userMessage = 'Camera access requires HTTPS. Please use a secure connection.';
      }
      
      this.onErrorCallback?.({ 
        message: userMessage, 
        originalError: err,
        type: 'camera_error',
      });
      
      throw err;
    }
  }

  /**
   * Stop the QR code scanner
   * 
   * @returns {Promise<void>}
   */
  async stop() {
    if (this.html5Qrcode && this.state === SCANNER_STATE.SCANNING) {
      try {
        await this.html5Qrcode.stop();
      } catch (err) {
        console.warn('[QRScanner] Error stopping scanner:', err);
      }
    }
    
    // Clear the scanner element
    if (this.html5Qrcode) {
      try {
        this.html5Qrcode.clear();
      } catch (err) {
        // Ignore clear errors
      }
    }
    
    this.state = SCANNER_STATE.STOPPED;
    this.html5Qrcode = null;
  }

  /**
   * Pause scanning (keeps camera active but stops decoding)
   * 
   * @returns {Promise<void>}
   */
  async pause() {
    if (this.html5Qrcode && this.state === SCANNER_STATE.SCANNING) {
      try {
        await this.html5Qrcode.pause(true);
        this.state = SCANNER_STATE.PAUSED;
      } catch (err) {
        console.warn('[QRScanner] Error pausing scanner:', err);
      }
    }
  }

  /**
   * Resume scanning after pause
   * 
   * @returns {Promise<void>}
   */
  async resume() {
    if (this.html5Qrcode && this.state === SCANNER_STATE.PAUSED) {
      try {
        await this.html5Qrcode.resume();
        this.state = SCANNER_STATE.SCANNING;
      } catch (err) {
        console.warn('[QRScanner] Error resuming scanner:', err);
      }
    }
  }

  /**
   * Get current scanner state
   * 
   * @returns {string} Current state
   */
  getState() {
    return this.state;
  }

  /**
   * Check if scanner is currently scanning
   * 
   * @returns {boolean}
   */
  isScanning() {
    return this.state === SCANNER_STATE.SCANNING;
  }

  /**
   * Scan a file/image for QR code
   * 
   * @param {File} imageFile - Image file to scan
   * @returns {Promise<string>} Decoded text
   */
  async scanFile(imageFile) {
    if (!this.html5Qrcode) {
      this.html5Qrcode = new Html5Qrcode(this.elementId);
    }

    try {
      const result = await this.html5Qrcode.scanFile(imageFile, false);
      return result;
    } catch (err) {
      console.error('[QRScanner] File scan error:', err);
      throw new Error('No QR code found in the image');
    }
  }
}

/**
 * Get available cameras
 * 
 * @returns {Promise<Array<{id: string, label: string}>>} List of available cameras
 */
export async function getAvailableCameras() {
  try {
    const devices = await Html5Qrcode.getCameras();
    return devices.map(device => ({
      id: device.id,
      label: device.label || `Camera ${device.id.slice(0, 8)}`,
    }));
  } catch (err) {
    console.error('[QRScanner] Error getting cameras:', err);
    return [];
  }
}

/**
 * Check if camera is supported
 * 
 * @returns {boolean}
 */
export function isCameraSupported() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

/**
 * React Hook for QR code scanning
 *
 * @param {object} options
 * @param {string} options.elementId - DOM element ID to render scanner into
 * @param {Function} options.onScan - Callback when QR code is scanned
 * @param {Function} options.onError - Callback on error
 * @param {boolean} [options.autoStart] - Whether to start scanning automatically
 * @param {object} [options.config] - Passed through to {@link QRScanner} constructor
 * @returns {Object} Scanner controls
 *
 * @example
 * ```jsx
 * function MyComponent() {
 *   const { start, stop, isScanning, error } = useQRScanner({
 *     elementId: 'qr-reader',
 *     onScan: (data) => console.log('Scanned:', data),
 *     onError: (err) => console.error(err),
 *   });
 *
 *   return (
 *     <div>
 *       <div id="qr-reader" style={{ width: '100%' }} />
 *       <button onClick={isScanning ? stop : start}>
 *         {isScanning ? 'Stop' : 'Start'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useQRScanner({
  elementId,
  onScan,
  onError,
  autoStart = false,
  config = {},
}) {
  // This is a factory function, not a real hook (to avoid circular dependency with React)
  // The actual hook implementation should be in a separate file if needed
  // For now, components can use the QRScanner class directly
  
  console.warn('[useQRScanner] This is a factory function. Use QRScanner class directly in React components.');
  
  const scanner = new QRScanner(elementId, config);
  
  return {
    scanner,
    start: (options) => scanner.start({ onSuccess: onScan, onError, ...options }),
    stop: () => scanner.stop(),
    pause: () => scanner.pause(),
    resume: () => scanner.resume(),
    getState: () => scanner.getState(),
    isScanning: () => scanner.isScanning(),
  };
}

// Export default scanner class
export default QRScanner;

